/**
 * PLT-5003: resolve which call-to-action the in-chat "usage limit reached"
 * card should show, from the signals the frontend has today.
 *
 * Background (PLT-4988): the eventual backend token-quota resolver will know
 * exactly which tier produced the 429, and the cleanest long-term design is
 * for it to put that on the 429 payload so the card reads it directly. That
 * resolver isn't built yet, so for Phase 1 we map the existing frontend
 * org/subscription signals to a CTA variant. The mapping below intentionally
 * mirrors the parent issue's tier table so it's easy to reconcile — and the
 * card consumes only the resolved variant, so swapping in a payload-provided
 * tier later is a one-line change.
 *
 * Variants → CTA (wording pending marketing/product blessing — open call #3):
 *  - 'starter'    → "Upgrade to Pro"               (self-serve upgrade)
 *  - 'pro'        → "Contact us to increase your limit"
 *  - 'enterprise' → "Contact your account manager"
 *
 * Mapping rationale (see PLT-4988 tier table):
 *  - Investor (AM_AO / PE_VC) and Company orgs are all Enterprise/100M by org
 *    type → 'enterprise'.
 *  - DMA Freemium defaults to Starter, upgraded by an active org-level
 *    subscription row (tier=pro → 'pro', tier=enterprise → 'enterprise').
 *  - Anything we can't positively classify (e.g. OTHER, or a DMA Consultant
 *    whose per-company tier we can't resolve here) falls back to 'pro', the
 *    neutral "contact us" CTA — never wrong-headed the way "Upgrade to Pro"
 *    (assumes self-serve) or "Contact your account manager" (assumes one
 *    exists) would be.
 *
 * Team users bypass the quota entirely and never reach this card, so the
 * team-true-by-default behaviour of the investor/company predicates is moot.
 */
import {
  useGetLoggedInUser,
  useIsCompanyUser,
  useIsDMAFreemiumUser,
  useIsInvestorUser,
} from '@upright/ui/common/generic/hooks';
import { useGetActiveSubscription } from '@upright/ui/views/dma-v2-view/shared/hooks/use-get-active-subscription';
import { findLicensedTokenLimitCtaVariant } from './licensed-token-limit-cta';

export type TokenLimitCtaVariant = 'starter' | 'pro' | 'enterprise';

export function useTokenLimitCtaVariant(): TokenLimitCtaVariant {
  const licensed = findLicensedTokenLimitCtaVariant(useGetLoggedInUser());
  const isInvestor = useIsInvestorUser();
  const isCompany = useIsCompanyUser();
  const isFreemium = useIsDMAFreemiumUser();

  // Only freemium orgs need the subscription row to disambiguate tier; skip
  // the request for everyone else.
  const { data: subscription } = useGetActiveSubscription({ enabled: isFreemium && !licensed });

  if (licensed) {
    return licensed;
  }

  if (isInvestor || isCompany) {
    return 'enterprise';
  }

  if (isFreemium) {
    const tier = subscription?.subscription.tier;
    if (tier === 'enterprise') return 'enterprise';
    if (tier === 'pro') return 'pro';
    return 'starter';
  }

  return 'pro';
}
